import { useCallback, useContext, useState } from 'react';
import { PBXCallState } from 'app/store/contexts';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import WebSocket from 'app/socket/WebSocket';
import { EventEmitter } from '../../events';

// Mirrors the header toggle's "go offline" branch (ToolbarLayout1's
// handleToggleChange) so logout can flip it without that component being
// mounted. Best-effort: a failed request shouldn't block logout, since the
// user is leaving anyway.
const setHeaderOffline = () =>
	new Promise(resolve => {
		localStorage.setItem('online', false);
		EventEmitter.dispatch('Online', false);
		CoreHttpHandler.request('core', 'offline', {}, resolve, resolve);
	});

// Shared by the user menu's "Logout" button and the idle-timeout "Logout"
// button so both go through the same PBX-disconnect and online-status
// teardown instead of drifting apart.
const useLogout = () => {
	const { disconnectForLogout } = useContext(PBXCallState) || {};
	const [loading, setLoading] = useState(false);

	const logout = useCallback(
		async ({ autoDisconnectPbx = false } = {}) => {
			// 1. Call-center agent: confirms (if connected/on-break) and disconnects,
			// unless the caller already asked to skip that prompt (the idle-timeout
			// dialog auto-disconnects instead of stacking a second confirmation on
			// top of its own "Are you still there?" modal).
			if (disconnectForLogout) {
				const canLogout = await disconnectForLogout({ skipConfirm: autoDisconnectPbx });
				if (!canLogout) return false;
			}

			setLoading(true);

			const user = JSON.parse(localStorage.getItem('user_data'));
			const auditLogId = JSON.parse(localStorage.getItem('audit_log_id'));
			let onlineStatus = JSON.parse(localStorage.getItem('online'));
			onlineStatus = onlineStatus === null ? false : onlineStatus;

			const isSuperAdmin = String(user?.client_id) === '0';

			// 2. Header online/offline toggle.
			if (!isSuperAdmin && onlineStatus === true) {
				await setHeaderOffline();
			}

			CoreHttpHandler.request(
				'core',
				'userLogout',
				{ key: ':login_id', value: auditLogId },
				() => {
					window.location.href = '/login';
					const settings = isSuperAdmin ? localStorage.getItem('settings') : null;
					WebSocket.destroy();
					localStorage.clear();
					if (isSuperAdmin) localStorage.setItem('settings', settings);
					setLoading(false);
				},
				() => {
					setLoading(false);
				}
			);

			return true;
		},
		[disconnectForLogout]
	);

	return { logout, loading };
};

export default useLogout;
